import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { api, type GlobalSearchHit, type GlobalSearchResults } from "../lib/api";
import { useIsDesktop } from "../lib/media";
import { queryKeys } from "../lib/queryKeys";
import { ticketIdFromTerm } from "../lib/ticket-id";
import { cn } from "./ui/cn";

/**
 * Search everything, from anywhere.
 *
 * Pulse is four products behind one rail, and until this existed each of them
 * hid its search inside its own screen -- so the first thing you had to decide,
 * on hearing a name down the phone, was which app that name lived in. This is
 * the answer to "I do not know which section it is in, and I should not have
 * to".
 *
 * ## One flat list, grouped by heading
 *
 * The four groups are headings over a single list rather than four panes or
 * four tabs, and that is what makes it keyboard-first: the down arrow walks
 * from the last company into the first ticket without anybody choosing a
 * column, and Enter opens whatever the walk landed on. Tabs would make "is it
 * a ticket or a call" a question to answer *before* searching, which is the
 * question this box exists to stop being asked.
 *
 * The order of the groups is fixed rather than ranked by score. A ranked list
 * would put a different kind of thing first for every term, so the row your
 * hand is already moving towards would move -- and with five rows per group
 * there is no scrolling to save. Companies lead because they are the thing
 * most often looked up and the thing everything else hangs off.
 *
 * ## It renders one row shape
 *
 * A ticket, an account, a call and a meeting have nothing in common as records
 * and exactly one thing in common as destinations: a line you recognise, a
 * line of context, and somewhere to go. The server reduces all four to that
 * shape -- see `global-search.service.ts` -- so there is no per-kind rendering
 * here to drift apart. The heading carries the difference, which is all the
 * difference a reader needs.
 */
const GROUPS = [
  { key: "companies", label: "Companies" },
  { key: "tickets", label: "Tickets" },
  { key: "calls", label: "Sales calls" },
  { key: "meetings", label: "Meetings" },
] as const satisfies readonly { key: keyof GlobalSearchResults; label: string }[];

/** Below this nothing is asked of the server. Mirrors the API's own floor. */
const MIN_TERM = 2;

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const [term, setTerm] = useState("");
  const isDesktop = useIsDesktop();
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => inputRef.current?.focus(), []);

  /*
   * Debounced, so a fast typist makes one request rather than nine. 180ms
   * matches the CRM's old box: below the point where a search feels laggy and
   * above the point where every keystroke is its own query -- and this one
   * fans out to four searches, so the saving is fourfold.
   */
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 180);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: queryKeys.globalSearch(debounced),
    queryFn: () => api.globalSearch(debounced),
    enabled: debounced.length >= MIN_TERM,
    staleTime: 30_000,
  });

  /*
   * The groups flattened once, and the flat list is what the arrow keys walk.
   *
   * Built here rather than at each key press so the index the highlight holds
   * and the index Enter resolves are the same list -- two flattenings, one per
   * render path, is how a palette ends up opening the row above the one that
   * was highlighted.
   */
  const rows = useMemo(() => {
    const flat: { hit: GlobalSearchHit; group: string }[] = [];
    for (const group of GROUPS) {
      for (const hit of results.data?.[group.key] ?? []) {
        flat.push({ hit, group: group.label });
      }
    }
    return flat;
  }, [results.data]);

  /*
   * Whether the agent has moved the highlight off where it started, by key.
   * Enter on an untouched "#1234" opens ticket 1234 whatever the rows say --
   * see `onKeyDown` -- but a row somebody walked to is the row they meant.
   */
  const moved = useRef(false);

  /* The term and the row highlighted when the rows last changed, so a
     refetch of the same term can put the highlight back on the same hit. */
  const highlighted = useRef<{ term: string; href?: string }>({ term: "" });
  useEffect(() => {
    highlighted.current = { term: debounced, href: rows[active]?.hit.href };
  }, [debounced, rows, active]);

  /*
   * A new term means the old highlight points at a different thing, so it
   * starts again at the top.
   *
   * Except where the term names a ticket outright: "#1234" is how the desk
   * writes a ticket id, so the highlight starts on that ticket and Enter opens
   * it. The groups keep their fixed order -- see above for why -- and only
   * the starting row moves, which costs nobody the row they were reaching for:
   * nobody types a `#` meaning a company.
   *
   * The same term with new rows -- a refetch on focus, or a result that
   * changed underneath -- is not a new search. The highlight follows the hit
   * it was on, wherever that now sits; snapping it to the top under somebody
   * arrowing down the list made Enter open a row they had walked past.
   */
  useEffect(() => {
    const previous = highlighted.current;
    if (previous.term === debounced && previous.href !== undefined) {
      const index = rows.findIndex((row) => row.hit.href === previous.href);
      if (index >= 0) {
        setActive(index);
        return;
      }
    }

    const named = ticketIdFromTerm(debounced);
    const index =
      named === null
        ? -1
        : rows.findIndex(
            (row) => row.group === "Tickets" && row.hit.id === named,
          );
    moved.current = false;
    setActive(Math.max(index, 0));
  }, [debounced, rows]);

  /*
   * Keep the highlight on screen when it is moved by keyboard.
   *
   * `block: "nearest"` so walking down the list scrolls by a row rather than
   * jumping the highlighted row to the middle of the box, which reads as the
   * list moving under you rather than you moving down it.
   */
  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-index="${active}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const open = (hit: GlobalSearchHit): void => {
    navigate(hit.href);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Escape") {
      onClose();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moved.current = true;
      setActive((index) => Math.min(index + 1, rows.length - 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moved.current = true;
      setActive((index) => Math.max(index - 1, 0));
    }
    if (event.key === "Enter") {
      event.preventDefault();

      /*
       * "#1234" and Enter opens ticket 1234, however fast it was typed.
       *
       * Faster than the debounce and the search behind it, the rows on screen
       * are still the previous term's, or none. Slower, the results may not
       * hold the ticket at all -- global search leaves spam out, for one --
       * and the highlight then starts on the first row, which is some company
       * or order that happened to match the digits. Either way the `#` has
       * already said which ticket is meant, so the outcome must not depend on
       * typing speed. Only a highlight the agent moved themselves wins over it.
       */
      const named = ticketIdFromTerm(term);
      const settled = term.trim() === debounced && results.isSuccess;
      const listed = rows.some(
        (row) => row.group === "Tickets" && row.hit.id === named,
      );
      if (named !== null && (!settled || (!listed && !moved.current))) {
        navigate(`/v/open/${named}`);
        onClose();
        return;
      }

      const row = rows[active];
      if (row) open(row.hit);
    }
  };

  const empty = results.isSuccess && rows.length === 0;

  return (
    /* `pt-24` was the whole story, and on a phone it is a quarter of the
       screen given to the scrim above a box whose list then has to fit in what
       is left -- with the keyboard up, almost nothing. Near the top on a
       phone, where it was on a desktop from `sm`. */
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-3 pt-4 sm:p-4 sm:pt-24">
      {/* Scrim as a button, so dismissing works by tap and by keyboard. */}
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />

      {/* Capped against the viewport rather than by a fixed list height, and
          `dvh` rather than `vh` because on iOS `vh` is the screen with the
          toolbars hidden -- the bottom of the list sat under them. */}
      <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-xl max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-8rem)]">
        {/*
          A Cancel beside the box on a phone, as every phone search has. There
          is no Escape key there, and tapping the strip of scrim the box leaves
          uncovered is a way out only for somebody who already knows it is one.

          The placeholder is shorter there too: the desktop's list of four
          kinds of thing was cut off mid-word at a phone's width, and a
          sentence that stops at "meetir" says less than "Search Pulse".
        */}
        <div className="flex shrink-0 items-center border-b border-line">
          <input
            ref={inputRef}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              isDesktop
                ? "Search companies, tickets, calls and meetings…"
                : "Search Pulse…"
            }
            aria-label="Search Pulse"
            enterKeyHint="search"
            className="min-w-0 flex-1 bg-transparent px-4 py-3 text-sm text-content outline-none placeholder:text-faint"
          />
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 px-4 py-3 text-sm text-brand-600 md:hidden dark:text-brand-400"
          >
            Cancel
          </button>
        </div>

        {/* `min-h-0`, without which the cap on the box above cannot take
            effect and the list simply grows past it. */}
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {debounced.length < MIN_TERM ? (
            <p className="px-4 py-6 text-center text-xs text-faint">
              Type at least two characters. A company, a person, a customer ID,
              a ticket number, an order reference.
            </p>
          ) : results.isLoading ? (
            <p className="px-4 py-6 text-center text-xs text-faint">Searching…</p>
          ) : empty ? (
            <p className="px-4 py-6 text-center text-xs text-faint">
              Nothing in Pulse matches “{debounced}”.
            </p>
          ) : (
            GROUPS.map((group) => {
              const hits = results.data?.[group.key] ?? [];
              if (hits.length === 0) return null;

              return (
                <section key={group.key}>
                  <p className="border-b border-line bg-surface-2 px-4 py-1 text-[11px] font-medium uppercase tracking-wide text-faint">
                    {group.label}
                  </p>
                  <ul>
                    {hits.map((hit) => {
                      /*
                       * The row's position in the *flat* list, which is what
                       * the arrows walk. Found rather than counted, so adding
                       * a group above cannot silently offset the highlight.
                       */
                      const index = rows.findIndex(
                        (row) => row.group === group.label && row.hit === hit,
                      );

                      return (
                        <li key={`${group.key}-${hit.id}`}>
                          <button
                            type="button"
                            data-index={index}
                            onClick={() => open(hit)}
                            onMouseEnter={() => setActive(index)}
                            className={cn(
                              "flex w-full flex-col gap-0.5 px-4 py-2 text-left",
                              index === active
                                ? "bg-surface-2"
                                : "hover:bg-surface-2",
                            )}
                          >
                            <span className="truncate text-sm text-content">
                              {hit.title}
                            </span>
                            {hit.subtitle ? (
                              <span className="truncate text-[11px] text-muted">
                                {hit.subtitle}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })
          )}
        </div>

        {/*
          The keys, said once at the bottom.

          Worth the strip: this is opened by people who have just learned it
          exists, and "arrows then Enter" is the part they would otherwise
          discover by trying the mouse first. Not on a phone, which has none of
          these keys and needed the line for results.
        */}
        <div className="hidden items-center gap-3 border-t border-line px-4 py-1.5 text-[10px] text-faint md:flex">
          <span>
            <kbd className="rounded border border-line px-1">↑</kbd>{" "}
            <kbd className="rounded border border-line px-1">↓</kbd> to move
          </span>
          <span>
            <kbd className="rounded border border-line px-1">↵</kbd> to open
          </span>
          <span>
            <kbd className="rounded border border-line px-1">esc</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
}
